import { useCallback, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { F0Box, TwoColumnLayout } from '@factorialco/f0-react'
import { IncentivesCompensationPlanDetailDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import { useStrictParams } from 'modules/core/hooks/useStrictParams'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

import { CompensationPlanAssignments } from './CompensationPlanAssignments'
import { CompensationPlanMetrics } from './CompensationPlanMetrics'
import { SendToCompensationDialog } from 'modules/incentives/components/SendToCompensationDialog'
import { SideContent } from './SideContent'
import { type DetailTab, Tabs } from './Tabs'
import { getCurrentPeriod, periodLabel } from 'modules/incentives/lib/period'
import { CompensationPlanDetailWidgets } from './Widgets'

export default function CompensationPlanDetail() {
  const { id } = useStrictParams<'id'>()
  const { company } = useCurrentSession()
  const [searchParams, setSearchParams] = useSearchParams()
  const modalOpen = searchParams.get('sendToCompensation') === 'true'
  const selectedPeriodParam = searchParams.get('periodId')
  const { data, loading, error } = useQuery(IncentivesCompensationPlanDetailDocument, {
    variables: { companyId: company.id, planId: String(id) },
  })

  const plan = data?.incentives?.compensationPlan
  const periods = plan?.compensationPlanPeriods ?? []
  const employeeCount = plan?.compensationPlanAssignmentsConnection.totalCount ?? 0

  const [activeTab, setActiveTab] = useState<DetailTab>('metrics')
  const currentPeriod = getCurrentPeriod(periods)
  const currentPeriodId = currentPeriod?.id
  const periodId = selectedPeriodParam ?? currentPeriodId

  const handlePeriodSelect = useCallback(
    (newPeriodId: string | null) => {
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev)
        if (newPeriodId) {
          newParams.set('periodId', newPeriodId)
        } else {
          newParams.delete('periodId')
        }
        return newParams
      })
    },
    [setSearchParams]
  )

  const selectedPeriod = periods.find((p) => p.id === periodId) ?? currentPeriod
  const selectedPeriodLabel =
    plan && selectedPeriod ? periodLabel(plan.recurrence, selectedPeriod) : ''

  const closeDialog = () => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev)
      newParams.delete('sendToCompensation')
      return newParams
    })
  }

  return (
    <ResponsivePage.Body loading={loading} error={error} variant="fullscreen">
      {periodId && (
        <SendToCompensationDialog
          isOpen={!!modalOpen}
          onClose={closeDialog}
          periodId={periodId}
          employeeCount={employeeCount}
          periodLabel={selectedPeriodLabel}
        />
      )}
      {plan && selectedPeriod && (
        <TwoColumnLayout
          mainColumnPosition="right"
          sideContent={
            <SideContent
              periods={periods}
              recurrence={plan.recurrence}
              currentPeriodId={currentPeriodId}
              selectedPeriodId={selectedPeriod.id}
              onPeriodSelect={handlePeriodSelect}
            />
          }
        >
          <F0Box display="flex" flexDirection="column" gap="2xl">
            <CompensationPlanDetailWidgets period={selectedPeriod} />
            <Tabs activeTab={activeTab} onTabChange={setActiveTab} />
            <F0Box marginTop="md">
              {activeTab === 'metrics' ? (
                <CompensationPlanMetrics periodId={selectedPeriod.id} />
              ) : (
                <CompensationPlanAssignments planId={String(id)} periodId={selectedPeriod.id} />
              )}
            </F0Box>
          </F0Box>
        </TwoColumnLayout>
      )}
    </ResponsivePage.Body>
  )
}
